import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ApiService } from '../../../../core/services/api.service';

import {
    trigger,
    state,
    style,
    transition,
    animate,
} from '@angular/animations';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { ToasterService } from '../../../../core/services/toaster.service';
import { SidebarService } from '../../../../core/services/sidebar.service';
import { AuthService } from '../../../../core/services/auth.service';

@Component({
    selector: 'app-role-menu-access',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        IconComponent,
        ButtonComponent,
        IndividualToasterComponent,
    ],
    templateUrl: './role-menu-access.component.html',
    styleUrls: ['./role-menu-access.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    animations: [
        trigger('expandCollapse', [
            state(
                'collapsed',
                style({ height: '0px', opacity: 0, visibility: 'hidden' })
            ),
            state(
                'expanded',
                style({ height: '*', opacity: 1, visibility: 'visible' })
            ),
            transition('expanded <=> collapsed', animate('300ms ease-in-out')),
        ]),
    ],
})
export class RoleMenuAccessComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private apiService = inject(ApiService);
    private toasterService = inject(ToasterService);
    private sidebarService = inject(SidebarService);
    private authService = inject(AuthService);

    roleId = signal<string | null>(null);
    roleName = signal<string>('Loading...');
    menuItems = signal<any[]>([]);
    isLoading = signal<boolean>(false);

    searchSubject = new Subject<string>();

    ngOnInit(): void {
        this.route.paramMap.subscribe(params => {
            const id = params.get('id');
            this.roleId.set(id);
            if (id) {
                this.fetchRoleDetails(id);
            }
        });

        this.route.queryParamMap.subscribe(params => {
            const role = params.get('role');
            if (role) {
                this.roleName.set(role);
            }
        });

        this.searchSubject
            .pipe(debounceTime(300), distinctUntilChanged())
            .subscribe(searchTerm => {
                const roleId = this.roleId();
                if (roleId) {
                    this.fetchRoleAccess(roleId, searchTerm);
                }
            });
    }

    onSearch(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.searchSubject.next(input.value);
    }

    toggleExpand(item: any): void {
        item.expanded = !item.expanded;
    }

    fetchRoleDetails(id: string): void {
        this.fetchRoleName(id);
        this.fetchRoleAccess(id);
    }

    fetchRoleAccess(id: string, search: string = ''): void {
        this.isLoading.set(true);

        const params: any = { roleId: id };
        if (search) {
            params.search = search;
        }

        this.apiService
            .get('/app-routes/list-role-route-access', params, {
                showLoader: false,
                showErrorToaster: true,
            })
            .subscribe({
                next: (response: any) => {
                    this.isLoading.set(false);

                    let rawData: any[] = [];
                    if (Array.isArray(response)) {
                        rawData = response;
                    } else if (Array.isArray(response?.data)) {
                        rawData = response.data;
                    } else if (Array.isArray(response?.roles)) {
                        rawData = response.roles;
                    }

                    const treeData = rawData.map((productGroup: any, index: number) => {
                        const shouldExpand = index === 0 || !!search;

                        return {
                            id: productGroup.product?.id,
                            name: productGroup.product?.name,
                            type: 'product',
                            expanded: shouldExpand,
                            hasAccess: false,
                            children: (productGroup.routes || []).map((route: any) => ({
                                id: route.id,
                                name: route.name,
                                type: 'route',
                                expanded: shouldExpand,
                                hasAccess: (route.roleAccess || []).some(
                                    (r: any) => r.hasAccess
                                ),
                                children: (route.subMenus || []).map((sub: any) => ({
                                    id: sub.id,
                                    name: sub.name,
                                    type: 'submenu',
                                    hasAccess: (sub.roleAccess || []).some(
                                        (r: any) => r.hasAccess
                                    ),
                                })),
                            })),
                        };
                    });

                    this.menuItems.set(treeData);
                },
                error: error => {
                    this.isLoading.set(false);
                    console.error('Error fetching role access:', error);
                },
            });
    }

    fetchRoleName(id: string): void {
        this.apiService
            .get('/roles/list', {}, { showLoader: false, showErrorToaster: false })
            .subscribe({
                next: (response: any) => {
                    let roles: any[] = [];

                    if (Array.isArray(response?.roles)) {
                        roles = response.roles;
                    } else if (Array.isArray(response?.data)) {
                        roles = response.data;
                    } else if (Array.isArray(response)) {
                        roles = response;
                    }

                    const role = roles.find(r => r.id === id);
                    if (role) {
                        this.roleName.set(role.name);
                    } else if (this.roleName() === 'Loading...') {
                        this.roleName.set(id);
                    }
                },
                error: () => {
                    if (this.roleName() === 'Loading...') {
                        this.roleName.set(id);
                    }
                },
            });
    }

    updateItemAccess(item: any, event: Event, parentItem: any = null): void {
        const isChecked = (event.target as HTMLInputElement).checked;
        item.hasAccess = isChecked;

        if (!parentItem && item.children?.length) {
            item.children.forEach((child: any) => {
                child.hasAccess = isChecked;
            });
        }

        if (parentItem) {
            parentItem.hasAccess = parentItem.children.some(
                (child: any) => child.hasAccess
            );
        }
    }

    saveChanges(): void {
        const roleId = this.roleId();
        if (!roleId) return;

        this.isLoading.set(true);

        const roleRouteAccess: any[] = [];

        this.menuItems().forEach(product => {
            product.children?.forEach((route: any) => {
                roleRouteAccess.push({
                    routeId: route.id,
                    hasAccess: route.hasAccess,
                });

                route.children?.forEach((sub: any) => {
                    roleRouteAccess.push({
                        routeId: sub.id,
                        hasAccess: sub.hasAccess,
                    });
                });
            });
        });

        this.apiService
            .post(
                '/app-routes/add-role-route-access',
                { roleId, roleRouteAccess },
                {
                    showLoader: true,
                    showSuccessToaster: true,
                    showErrorToaster: true,
                }
            )
            .subscribe({
                next: () => {
                    this.isLoading.set(false);
                    this.toasterService.success(
                        'Role access updated successfully',
                        'Success',
                        2000
                    );
                    this.authService.authenticate(true).subscribe((res) => {
                        if (res) {
                            this.sidebarService.callSidebarMethod();
                        }
                    })
                    this.fetchRoleDetails(roleId);
                },
                error: err => {
                    this.isLoading.set(false);
                    console.error('Error updating role access:', err);
                },
            });
    }
}
